import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/events", label: "Events" },
  { to: "/shop", label: "Shop" },
  { to: "/pet-record", label: "Pet Record" },
  { to: "/how-its-made", label: "How It's Made" },
  { to: "/community", label: "Community" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const [contactEmail, setContactEmail] = useState("");

  useEffect(() => {
    api.get("/site-settings").then((r) => setContactEmail(r.data.contact_email || ""));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-brand-cream text-brand-soft">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-brand-cream/85 border-b border-brand-maroon/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Furshala" className="w-14 h-14 object-contain" />
            <div className="leading-tight">
              <div className="font-serif text-2xl tracking-tight text-brand-maroon">Furshala</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-brand-gold font-semibold">Natural · Wellness · Love</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-brand-maroon ${pathname === n.to ? "text-brand-maroon" : "text-brand-soft/75"}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <button
            className="md:hidden p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-brand-maroon/10 bg-brand-cream">
            <div className="px-6 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="py-2 text-sm font-medium"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  {n.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-brand-soft text-brand-cream mt-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="Furshala" className="w-14 h-14 object-contain" />
            <div className="font-serif text-2xl">Furshala</div>
          </div>
            <p className="text-sm text-brand-cream/70 leading-relaxed">
              A pet community for the curious-hearted. Curated events. Naturally handcrafted.
            </p>
          </div>
          <div>
            <div className="eyebrow text-brand-saffron mb-3">Explore</div>
            <ul className="space-y-2 text-sm text-brand-cream/80">
              <li><Link to="/events" className="hover:text-brand-saffron">Events</Link></li>
              <li><Link to="/shop" className="hover:text-brand-saffron">Shop</Link></li>
              <li><Link to="/how-its-made" className="hover:text-brand-saffron">How It&apos;s Made</Link></li>
              <li><Link to="/community" className="hover:text-brand-saffron">Community</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow text-brand-saffron mb-3">Policies</div>
            <ul className="space-y-2 text-sm text-brand-cream/80">
              <li><Link to="/refund-policy" className="hover:text-brand-saffron">Cancellation & Refund</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-brand-saffron">Privacy Policy</Link></li>
              <li><Link to="/admin/login" className="hover:text-brand-saffron">Admin</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow text-brand-saffron mb-3">Stay Close</div>
            {contactEmail ? (
              <p className="text-sm text-brand-cream/70">
                Drop us a line at <a className="underline hover:text-brand-saffron" href={`mailto:${contactEmail}`}>{contactEmail}</a>
              </p>
            ) : (
              <p className="text-sm text-brand-cream/70">Reach out via our Telegram and WhatsApp channels.</p>
            )}
          </div>
        </div>
        <div className="border-t border-brand-cream/10">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 py-5 text-xs flex flex-col sm:flex-row sm:justify-between gap-2 text-brand-cream/50">
            <div>© {new Date().getFullYear()} Furshala. Made with love for paws.</div>
            <div>Natural · Wellness · Love</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
